import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useV2Translation } from '../i18n';
import { SECTION_LABEL_KEYS, V2_ROUTES } from '../routes';
import { NavItemView, useV2Nav } from '../useV2Nav';
import { NAV_ICONS, LockIcon, SearchIcon } from './icons';
import { cx } from './styles';

/**
 * ⌘K / Ctrl+K navigation launcher (DESIGN_PLAN §2.10).
 *
 * NAVIGATION ONLY. It lists exactly the routes the current user can see, plus
 * `Create Dice Account`, and it fires `navigate()` and nothing else. It
 * deliberately does not invoke page actions (Export CSV, Sync all, Transfer
 * Multiple): those live in page state, and firing mutations from the shell
 * would be new wiring and a functionality change in all but name.
 */

interface PaletteEntry {
  item: NavItemView;
  group: string;
}

export const CommandPalette: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const { t } = useV2Translation();
  const navigate = useNavigate();
  const { sections } = useV2Nav();
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const restoreFocusTo = useRef<Element | null>(null);

  const entries = useMemo<PaletteEntry[]>(() => {
    const out: PaletteEntry[] = [];
    sections.forEach((section) => {
      section.items.forEach((item) => out.push({ item, group: section.label }));
      // `Create Dice Account` is a child of Dice Accounts, so it belongs in the
      // Platforms group rather than a group of its own.
      if (section.id === 'platforms') {
        const signup = V2_ROUTES.find((r) => r.id === 'dice-signup');
        if (signup) {
          out.push({
            item: {
              id: signup.id,
              path: signup.path,
              label: t(signup.labelKey),
              icon: signup.icon,
              locked: false,
            },
            group: t(SECTION_LABEL_KEYS.platforms),
          });
        }
      }
    });
    return out;
  }, [sections, t]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => e.item.label.toLowerCase().includes(q));
  }, [entries, query]);

  // Reset on each open, and remember what to hand focus back to on close.
  useEffect(() => {
    if (!open) return;
    restoreFocusTo.current = document.activeElement;
    setQuery('');
    setIndex(0);
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (open) return;
    const el = restoreFocusTo.current;
    if (el instanceof HTMLElement) el.focus();
  }, [open]);

  useEffect(() => {
    setIndex(0);
  }, [query]);

  const go = useCallback(
    (entry: PaletteEntry) => {
      onClose();
      navigate(entry.item.path);
    },
    [navigate, onClose],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIndex((i) => (filtered.length === 0 ? 0 : (i + 1) % filtered.length));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndex((i) => (filtered.length === 0 ? 0 : (i - 1 + filtered.length) % filtered.length));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const entry = filtered[index];
      if (entry) go(entry);
      return;
    }
    if (e.key === 'Tab') {
      // Trap focus inside the panel.
      const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
        'input, button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  // Keep the highlighted row in view when arrowing past the panel edge.
  useEffect(() => {
    const active = listRef.current?.querySelector('[data-active="true"]');
    if (active instanceof HTMLElement) active.scrollIntoView({ block: 'nearest' });
  }, [index]);

  if (!open) return null;

  let lastGroup: string | null = null;

  return (
    <div className="fixed inset-0 z-[70]">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('app.navigation.commandPalette')}
        onKeyDown={onKeyDown}
        className="relative mx-auto mt-[15vh] w-[calc(100%-2rem)] max-w-lg overflow-hidden rounded-2xl border border-white/10 bg-[#121212] shadow-2xl shadow-black/60"
      >
        <div className="flex items-center gap-3 border-b border-white/[0.08] px-4">
          <SearchIcon className="h-4 w-4 flex-shrink-0 text-white/30" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('app.navigation.search')}
            aria-label={t('app.navigation.search')}
            className="w-full bg-transparent py-3.5 text-sm text-white outline-none placeholder:text-white/30"
          />
          <kbd className="flex-shrink-0 rounded border border-white/10 px-1.5 py-0.5 text-[10px] text-white/30">
            Esc
          </kbd>
        </div>

        <div ref={listRef} className="max-h-[50vh] overflow-y-auto p-2">
          {filtered.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-white/40">No matches</p>
          )}
          {filtered.map((entry, i) => {
            const Icon = NAV_ICONS[entry.item.icon];
            const showGroup = entry.group !== lastGroup;
            lastGroup = entry.group;
            const active = i === index;
            return (
              <React.Fragment key={`${entry.item.id}-${entry.group}`}>
                {showGroup && (
                  <p className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/30">
                    {entry.group}
                  </p>
                )}
                <button
                  type="button"
                  data-active={active}
                  onMouseEnter={() => setIndex(i)}
                  onClick={() => go(entry)}
                  title={entry.item.title}
                  className={cx(
                    'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                    active ? 'bg-[#3b82f6]/10 text-white' : 'text-white/60 hover:bg-white/[0.04]',
                  )}
                >
                  <Icon
                    className={cx(
                      'h-[18px] w-[18px] flex-shrink-0',
                      active ? 'text-[#60a5fa]' : 'text-white/35',
                    )}
                  />
                  <span className="min-w-0 flex-1 truncate">{entry.item.label}</span>
                  {entry.item.beta && (
                    <span className="flex-shrink-0 rounded border border-[#3b82f6]/25 bg-[#3b82f6]/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#60a5fa]">
                      Beta
                    </span>
                  )}
                  {entry.item.locked && (
                    <span className="flex flex-shrink-0 items-center gap-1.5">
                      <LockIcon className="h-3 w-3 text-white/25" />
                      {entry.item.tierChip && (
                        <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-[10px] text-white/40">
                          {entry.item.tierChip}
                        </span>
                      )}
                    </span>
                  )}
                </button>
              </React.Fragment>
            );
          })}
        </div>

        <div className="flex items-center gap-4 border-t border-white/[0.08] px-4 py-2 text-[11px] text-white/30">
          <span>↑↓ move</span>
          <span>↵ go</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
};
